// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useTabStore } from './tabStore';

export interface AuthUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly tenant: string;
  readonly firmCode: string;
  readonly role: string;
  readonly isLicenseOwner?: boolean;
  readonly needsOnboarding?: boolean;
  readonly licenseCode?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  /** Profil fotografinin adresi — yoksa arayuz bas harfe duser. */
  readonly avatarUrl?: string | null;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  rememberMe: boolean;
  setAuth: (token: string, user: AuthUser, rememberMe?: boolean) => void;
  /** Acik oturumdaki kullanici alanlarini gunceller (profil fotografi gibi). */
  patchUser: (patch: Partial<AuthUser>) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

// JWT exp claim okur — token suresi gecmisse oto-logout icin.
function isTokenExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 < Date.now();
  } catch {
    return false;
  }
}

// Tarayici acilirken localStorage'da 'rememberMe' bayragi varsa orayi
// kaynak yap, yoksa sessionStorage. Tarayici kapatildiginda
// sessionStorage temizlenir → kullanici yeniden giris yapar.
function pickStorage(): Storage {
  try {
    const flag = localStorage.getItem('elpo-core-auth-remember');
    if (flag === '1') return localStorage;
  } catch { /* private mode */ }
  return sessionStorage;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      rememberMe: false,
      patchUser: (patch) => {
        // Profil fotografi degisince kullanicinin yeniden giris yapmasini beklemeyiz:
        // fotograf ust cubukta, yorumlarda ve asistanda ANINDA gorunur.
        const mevcut = get().user;
        if (!mevcut) return;
        set({ user: { ...mevcut, ...patch } });
      },
      setAuth: (token, user, rememberMe = false) => {
        // 'Beni hatirla' kullanicinin secimi: true → localStorage, false → sessionStorage
        try {
          if (rememberMe) {
            localStorage.setItem('elpo-core-auth-remember', '1');
          } else {
            localStorage.removeItem('elpo-core-auth-remember');
            // Eski sessiondan kalan kalıntı varsa temizle
            localStorage.removeItem('elpo-core-auth');
          }
        } catch { /* private mode */ }
        // Yeni girişte eski oturumun açık sekmelerini sıfırla — sadece aktif sayfa sekme olsun.
        try { useTabStore.getState().resetTabs(); sessionStorage.removeItem('elpo-page-tabs'); } catch { /* */ }
        // HER GİRİŞTE sol menü KAPALI başlar (kullanıcı kararı 2026-08-09). Oturum içinde
        // açtığı gruplar korunur (sayfa değişince kapanmaz); kapanma yalnız yeni girişte olur.
        try { localStorage.removeItem('elpo.sidebar.openGroups'); } catch { /* private mode */ }
        set({ token, user, rememberMe });
      },
      logout: () => {
        try {
          localStorage.removeItem('elpo-core-auth-remember');
          localStorage.removeItem('elpo-core-auth');
          sessionStorage.removeItem('elpo-core-auth');
        } catch { /* private mode */ }
        try { useTabStore.getState().resetTabs(); sessionStorage.removeItem('elpo-page-tabs'); } catch { /* */ }
        // Çıkışta da temizlenir: bir sonraki giriş hangi yoldan olursa olsun menü kapalı açılsın.
        try { localStorage.removeItem('elpo.sidebar.openGroups'); } catch { /* private mode */ }
        set({ token: null, user: null, rememberMe: false });
      },
      isAuthenticated: () => {
        const t = get().token;
        if (!t) return false;
        if (isTokenExpired(t)) {
          // Otomatik logout: token suresi gecmis
          get().logout();
          return false;
        }
        return true;
      },
    }),
    {
      name: 'elpo-core-auth',
      version: 2,
      storage: createJSONStorage(pickStorage),
    },
  ),
);
